/**
 * History-API navigation for svelte-spa-router.
 * Upstream Router is hash-only; we keep the package (wrap + route map) and drive
 * pathname via pushState / popstate — URLs stay /login, /dashboard/...
 */
import { readable, writable, derived } from 'svelte/store'
import { tick } from 'svelte'
import { defaultPathForSite, SITE_ORIGINS } from './hostSite.js'

function getLocation() {
  const path = window.location.pathname || '/'
  const qs = (window.location.search || '').replace(/^\?/, '')
  return { location: path, querystring: qs }
}

function fallbackPath() {
  try {
    return defaultPathForSite()
  } catch {
    return '/dashboard/overview'
  }
}

/**
 * History API only accepts same-document paths (`/dashboard/...`).
 * Auth/Caddy often hand absolute `https://dashboard…/hub` in `?next=` / redirect —
 * collapse same-origin absolutes to pathname(+search).
 * Cross-origin product hosts return null — caller should hard-navigate.
 */
export function spaPath(next, fallback) {
  const fb =
    fallback && String(fallback).charAt(0) === '/' ? String(fallback) : fallbackPath()
  if (next == null || next === '') return fb
  const raw = String(next).trim()
  if (!raw) return fb
  if (raw.charAt(0) === '/' && !raw.startsWith('//')) {
    return raw
  }
  try {
    const u = new URL(raw, window.location.origin)
    if (u.origin === window.location.origin) {
      return `${u.pathname || '/'}${u.search || ''}` || fb
    }
    // Known product hosts — signal caller to hard-nav (return null).
    const host = (u.hostname || '').toLowerCase()
    const known = new Set([
      'dashboard.lunes-crypto.ru',
      'paper.lunes-crypto.ru',
      'live.lunes-crypto.ru',
    ])
    if (known.has(host) && u.protocol === 'https:') {
      return null
    }
    return fb
  } catch {
    return fb
  }
}

/** Absolute https URL for cross-subdomain post-login next (or null). */
export function absoluteNextUrl(next) {
  if (next == null || next === '') return null
  const raw = String(next).trim()
  if (!raw || (raw.charAt(0) === '/' && !raw.startsWith('//'))) return null
  try {
    const u = new URL(raw, window.location.origin)
    if (u.origin === window.location.origin) return null
    const host = (u.hostname || '').toLowerCase()
    const known = new Set(Object.values(SITE_ORIGINS).map((o) => new URL(o).hostname))
    // SITE_ORIGINS.landing is apex — not a product SPA next target
    known.delete('lunes-crypto.ru')
    if (known.has(host) && u.protocol === 'https:') {
      return u.toString()
    }
  } catch {
    /* ignore */
  }
  return null
}

/**
 * Navigate after login/OTP: same-host history API, or hard nav to paper/live/dashboard.
 * @param {string | null | undefined} next
 * @param {string} [fallback]
 */
export async function goAfterAuth(next, fallback) {
  const abs = absoluteNextUrl(next)
  if (abs) {
    window.location.replace(abs)
    return
  }
  const path = spaPath(next, fallback || fallbackPath())
  if (!path) {
    const again = absoluteNextUrl(next)
    if (again) {
      window.location.replace(again)
      return
    }
    await replace(fallback || fallbackPath())
    return
  }
  await replace(path)
}

export const loc = readable(null, (set) => {
  set(getLocation())
  const update = () => set(getLocation())
  window.addEventListener('popstate', update)
  window.addEventListener('lunes:navigate', update)
  return () => {
    window.removeEventListener('popstate', update)
    window.removeEventListener('lunes:navigate', update)
  }
})

export const location = derived(loc, ($l) => ($l ? $l.location : '/'))
export const querystring = derived(loc, ($l) => ($l ? $l.querystring : ''))
export const params = writable(undefined)

function notify() {
  window.dispatchEvent(new Event('lunes:navigate'))
}

export async function push(path) {
  const url = spaPath(path)
  if (!url || url.charAt(0) !== '/') throw new Error('Invalid location')
  await tick()
  window.history.pushState(window.history.state || {}, '', url)
  notify()
}

export async function replace(path) {
  const url = spaPath(path)
  if (!url || url.charAt(0) !== '/') throw new Error('Invalid location')
  await tick()
  window.history.replaceState(window.history.state || {}, '', url)
  notify()
}

export async function pop() {
  await tick()
  window.history.back()
}

export function link(node, opts) {
  opts = typeof opts === 'string' ? { href: opts } : opts || {}
  if (!node || node.tagName.toLowerCase() !== 'a') {
    throw new Error('link action only works on <a>')
  }
  function apply(o) {
    const href = o.href || node.getAttribute('href')
    if (!href || href.charAt(0) !== '/') {
      throw new Error('href must start with /')
    }
    node.setAttribute('href', href)
  }
  apply(opts)
  const onClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    if (opts.disabled) return
    const href = node.getAttribute('href')
    push(href)
  }
  node.addEventListener('click', onClick)
  return {
    update(u) {
      opts = typeof u === 'string' ? { href: u } : u || {}
      apply(opts)
    },
    destroy() {
      node.removeEventListener('click', onClick)
    },
  }
}

export { wrap } from 'svelte-spa-router/wrap'
